/**
 * Black or white for a product card's name and price, read from the photo
 * under them (docs/adr/0012), with assets/birrot-ink.js as the home hero's
 * button is (docs/adr/0010).
 *
 * Each text gets `data-birrot-ink="dark|light"`; birrot.css maps it to a
 * colour, dark (--birrot-ink) being the first paint and what a card shows
 * when its photo cannot be read. Where birrot.css stacks the slides to fade
 * the second photo in on hover (from 1025px with a mouse), that photo is
 * read too, into `data-birrot-ink-hover`, which the same hover selects;
 * elsewhere, or with one photo, it repeats the first; until the second
 * photo has loaded, hover still shows the first, and so does its ink. The
 * photos read are the ones on the page, as they load. A card is read once
 * it comes near the viewport, again when a variant moves its slideshow
 * (`slideshow:select`), and again when it next nears the viewport after a
 * resize, which can stack the price under the name or start the fade.
 * The colours and sizes row (blocks/birrot-card-variants.liquid), which
 * hover swaps in for the name and price, is read the same way: it is laid
 * out while unseen, so its box is known, and its hover ink is the one on
 * show. Below 750px, and without a mouse at any width, the text and the
 * heart sit under the photo (birrot.css, "Mobile"), in the page's ink, so
 * only the sizes and colours a swipe brings up over the photo are read
 * there, from the photo in view; the rest are read when a resize takes the
 * card past 750px. Wherever the
 * slides are not stacked (below 1025px, or without a mouse) the track
 * swipes, and the photo in view is the one read. Once a chevron
 * has stepped the card on to another photo (assets/birrot-card-photos.js),
 * that photo is the one hover shows, and its ink the hover ink; each step,
 * and each swipe to another photo, is a `birrot:card-photo` event, on
 * which the card is read again. Cards rendered
 * later (the recommendations are morphed in) are picked up as they arrive,
 * and so are cards a filter or sort morphs in place: the morph drops the
 * attributes this script set, which nothing else removes. A card whose
 * latest read has ended, in a colour or in failure, carries
 * `data-birrot-inked`. The test harness, which scrolls faster than the
 * observer reports, asks for every card at once with a `birrot:ink-cards`
 * event on document and waits for the attribute.
 */

import { debounce } from '@theme/utilities';

const script = /** @type {HTMLScriptElement} */ (document.querySelector('script[data-birrot-ink-module]'));

/** The texts over the photo, by their key in the sample. */
const TEXTS = {
  title: ".product-grid__card [class*='product_title']",
  price: '.product-grid__card product-price',
  // The favorite heart (blocks/birrot-favorite.liquid): an icon, no text;
  // its box is the icon's own (textBox).
  heart: '.product-grid__card > .birrot-favorite',
  // Colours and sizes (blocks/birrot-card-variants.liquid): text and dots.
  variants: '.product-grid__card > .birrot-card-variants',
};

/** Where the text sits under the photo rather than over it (birrot.css, "Mobile"): below 750px, and a tablet. */
const UNDER_PHOTO = window.matchMedia('(max-width: 749px), not all and (hover: hover) and (pointer: fine)');

/** Picks kept, by photo and layout, so a read of the same photo again is free; the oldest go first. */
const PICKS_KEPT = 400;

/** @typedef {{ left: number, top: number, width: number, height: number }} Box */

/** @type {Promise<typeof import('./birrot-ink.js')> | null} */
let inkModule = null;

/**
 * Each card's latest read and its key: an older read that ends later is
 * dropped, and a second ask for the same one waits on it.
 *
 * @type {WeakMap<Element, { key: string, read: Promise<void> }>}
 */
const latest = new WeakMap();

/** @param {Element} card @param {string} key */
const isLatest = (card, key) => latest.get(card)?.key === key;

/** @typedef {Record<string, 'light' | 'dark'>} Pick */

/**
 * One pick per photo and layout (the gallery's size and the texts' boxes),
 * whichever role the photo plays: a chevron's step on to a third photo, or
 * back to the first, reads only the photo it has not read in that layout.
 * A photo that failed or timed out is not remembered, so a read again
 * (when it comes in after all) reads it alone.
 *
 * @type {Map<string, Pick>}
 */
const picks = new Map();

/**
 * The photo's `src` attribute, which names it before it loads and after
 * (`currentSrc` is empty until then, and is a rendition after), so a read
 * keeps its key.
 *
 * @param {HTMLImageElement | null | undefined} image
 */
const source = (image) => image?.getAttribute('src') || null;

/**
 * The photo on show and the one hover fades in, if any. Where birrot.css
 * stacks the slides for the fade (a grid; asked of the page, so the
 * breakpoint lives in the stylesheet alone), the first two shown slides;
 * elsewhere the slideshow's current one, which a variant can move. These
 * are the page's own images (snippets/card-gallery.liquid loads them with
 * `crossorigin`), read once they load: no copy is fetched.
 *
 * @param {Element} card
 * @returns {{ rest: HTMLImageElement | null, hover: HTMLImageElement | null }}
 */
function photos(card) {
  const only = /** @type {HTMLImageElement | null} */ (card.querySelector('.card-gallery img'));
  const slides = card.querySelector('.card-gallery slideshow-slides');
  if (slides && getComputedStyle(slides).display === 'grid') {
    const shown = /** @type {NodeListOf<HTMLImageElement>} */ (slides.querySelectorAll('slideshow-slide:not([hidden]) img'));
    // A chevron's choice (assets/birrot-card-photos.js) is the hover's photo.
    const stepped = /** @type {HTMLImageElement | null} */ (
      card.querySelector('.card-gallery[data-birrot-stepped] slideshow-slide[data-birrot-shown]:not([hidden]) img')
    );
    return { rest: shown[0] ?? only, hover: stepped ?? shown[1] ?? null };
  }
  // Wherever the slides are not stacked the track is swiped (Horizon's own
  // scroll, at every width below the stack: a tablet, or a laptop's
  // trackpad), and the slide in view is marked (assets/birrot-card-photos.js);
  // the disabled slideshow's `current` stays at 0 through a swipe.
  const swiped = /** @type {HTMLImageElement | null} */ (
    card.querySelector('.card-gallery slideshow-slide[data-birrot-shown] img')
  );
  if (swiped) return { rest: swiped, hover: null };
  const slideshow = /** @type {any} */ (card).refs?.slideshow;
  const slide = slideshow?.slides?.[slideshow.current];
  return { rest: slide?.querySelector('img') ?? only, hover: null };
}

/**
 * The box round an element's visible text and drawings (the heart's icon,
 * the colour dots), relative to `origin`. The text block itself is as wide
 * as its grid column and padded, and the price holds visually hidden
 * labels ("Sale price") that would widen a range over the whole element.
 *
 * @param {Element} element
 * @param {DOMRect} origin
 * @returns {Box | null}
 */
function textBox(element, origin) {
  let left = Infinity;
  let top = Infinity;
  let right = -Infinity;
  let bottom = -Infinity;
  const range = document.createRange();
  const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT);
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    if (!node.textContent?.trim() || node.parentElement?.closest('.visually-hidden')) continue;
    range.selectNodeContents(node);
    const rect = range.getBoundingClientRect();
    if (!rect.width || !rect.height) continue;
    left = Math.min(left, rect.left);
    top = Math.min(top, rect.top);
    right = Math.max(right, rect.right);
    bottom = Math.max(bottom, rect.bottom);
  }
  for (const drawing of element.querySelectorAll('svg, img')) {
    const rect = drawing.getBoundingClientRect();
    if (!rect.width || !rect.height) continue;
    left = Math.min(left, rect.left);
    top = Math.min(top, rect.top);
    right = Math.max(right, rect.right);
    bottom = Math.max(bottom, rect.bottom);
  }
  if (left === Infinity) return null;
  return { left: left - origin.left, top: top - origin.top, width: right - left, height: bottom - top };
}

/**
 * Set an attribute only when it changes: each write is a record for the
 * observer below, and a style invalidation.
 *
 * @param {HTMLElement} element
 * @param {string} name
 * @param {string} value
 */
function put(element, name, value) {
  if (element.dataset[name] !== value) element.dataset[name] = value;
}

/**
 * The card's latest read has ended. `data-birrot-inking` (outside the
 * observer's filter) is on while a newer read runs, so the harness does not
 * take an earlier colour for this one.
 *
 * @param {HTMLElement} card
 */
function markInked(card) {
  put(card, 'birrotInked', '');
  delete card.dataset.birrotInking;
}

/**
 * Read the card as it is now. Resolves once this read, or the identical one
 * already running, has ended; never rejects.
 *
 * @param {HTMLElement} card
 * @returns {Promise<void>}
 */
function ink(card) {
  const { rest, hover } = photos(card);
  const src = source(rest);
  const area = card.querySelector('.card-gallery')?.getBoundingClientRect();

  /** @type {Map<string, { element: HTMLElement, box: Box }>} */
  const texts = new Map();
  if (rest && src && area?.width && area.height) {
    for (const [name, selector] of Object.entries(TEXTS)) {
      // Under the photo (birrot.css, "Mobile") everything is in the
      // page's ink but the sizes and colours a swipe brings up over it.
      if (UNDER_PHOTO.matches && name !== 'variants') continue;
      const element = /** @type {HTMLElement | null} */ (card.querySelector(selector));
      const box = element && textBox(element, area);
      if (element && box) texts.set(name, { element, box });
    }
  }
  if (!rest || !texts.size) {
    // Nothing to read: no photo, or no text over it.
    latest.delete(card);
    markInked(card);
    return Promise.resolve();
  }

  const boxes = Object.fromEntries([...texts].map(([name, { box }]) => [name, box]));
  const key = `${src} ${source(hover)} ${Math.round(area.width)}x${Math.round(area.height)} ${JSON.stringify(boxes)}`;
  const current = latest.get(card);
  if (current?.key === key) return current.read;
  // Recorded before the read starts: a cached pick applies at once, and
  // checks it is still the latest.
  const entry = { key, read: Promise.resolve() };
  latest.set(card, entry);
  put(card, 'birrotInking', '');
  entry.read = sample(card, key, { rest, hover, area: /** @type {DOMRect} */ (area), boxes }, texts);
  return entry.read;
}

/**
 * A card photo that has loaded carries this: the sign the photo on the page
 * is the one its markup names. `complete` and `naturalWidth` still describe
 * the previous photo after a morph changed the `src`. The same contract as
 * assets/birrot-product-card.js (which sets it on `load` and takes it off
 * when the source changes), assets/birrot-ink.js (`loaded`, which waits for
 * it) and birrot.css (which fades the second photo in on it).
 */
const LOADED = 'data-birrot-loaded';

/** @param {HTMLImageElement} image */
const isLoaded = (image) => image.hasAttribute(LOADED);

/** Cards waiting on a photo's `load` to be read again: one listener each. */
const awaiting = new WeakSet();

/**
 * Read the card again when one of its photos next loads: one that failed,
 * which birrot-product-card.js asks for again (as a new element, hence the
 * capture on the card rather than a listener on the image), or one that
 * took too long.
 *
 * @param {HTMLElement} card
 */
function again(card) {
  if (awaiting.has(card)) return;
  awaiting.add(card);
  card.addEventListener(
    'load',
    () => {
      awaiting.delete(card);
      latest.delete(card);
      ink(card);
    },
    { capture: true, once: true }
  );
}

/** The ink module, imported on first use; a failed import is tried again next time. */
function inkLibrary() {
  inkModule ??= import(script.dataset.birrotInkModule).catch((error) => {
    inkModule = null;
    throw error;
  });
  return inkModule;
}

/**
 * @param {HTMLElement} card
 * @param {string} key
 * @param {{ rest: HTMLImageElement, hover: HTMLImageElement | null, area: DOMRect, boxes: Record<string, Box> }} photo
 * @param {Map<string, { element: HTMLElement, box: Box }>} texts
 */
async function sample(card, key, { rest, hover, area, boxes }, texts) {
  const layout = `${Math.round(area.width)}x${Math.round(area.height)} ${JSON.stringify(boxes)}`;
  const restKey = `${source(rest)} ${layout}`;
  const hoverKey = `${source(hover)} ${layout}`;

  /** @param {HTMLImageElement} image @returns {Promise<Pick>} */
  const read = async (image) => {
    const { inkOver, WHITE } = await inkLibrary();
    return Object.fromEntries(
      Object.entries(await inkOver(image, area, boxes)).map(([name, colour]) => [name, colour === WHITE ? 'light' : 'dark'])
    );
  };

  /** @type {Pick} */
  let first;
  try {
    first = picks.get(restKey) ?? (await read(rest));
  } catch {
    // The photo failed, or could not be read: dark, the first paint, not
    // remembered, and read again if the photo comes in after all.
    if (!isLatest(card, key)) return;
    latest.delete(card);
    const dark = Object.fromEntries([...texts.keys()].map((name) => [name, 'dark']));
    finish(card, texts, { rest: dark, hover: dark });
    again(card);
    return;
  }
  // A newer read has started since; it marks the card when it ends.
  if (!isLatest(card, key)) return;
  // Kept whatever the second photo does: a read again only reads that one.
  remember(restKey, first);
  // No second photo, or a chevron stepped back to the first: one read.
  if (!hover || hover === rest) {
    finish(card, texts, { rest: first, hover: first });
    return;
  }
  const known = picks.get(hoverKey);
  if (known && isLoaded(hover)) {
    finish(card, texts, { rest: first, hover: known });
    return;
  }

  // Until the second photo is in, hover keeps the first on show
  // (birrot.css), so its ink holds for hover too; the card stays
  // `data-birrot-inking` until the second's is known. A known pick only
  // waits for the photo to load. A text already holding a hover ink keeps
  // it meanwhile: a chevron's step shows its photo only once loaded, and
  // the first photo's ink would flash between the two.
  paint(texts, { rest: first, hover: first }, true);
  /** @type {Pick | null} */
  let second = null;
  try {
    if (known) {
      const { loaded } = await inkLibrary();
      await loaded(hover);
      second = known;
    } else {
      second = await read(hover);
    }
  } catch {
    // Left on the first photo's ink, and read again if it comes in.
  }
  if (!isLatest(card, key)) return;
  if (!second) {
    finish(card, texts, { rest: first, hover: first });
    latest.delete(card);
    again(card);
    return;
  }
  remember(hoverKey, second);
  finish(card, texts, { rest: first, hover: second });
}

/** @param {string} key @param {Pick} pick */
function remember(key, pick) {
  picks.set(key, pick);
  if (picks.size > PICKS_KEPT) picks.delete(picks.keys().next().value);
}

/**
 * @param {Map<string, { element: HTMLElement }>} texts
 * @param {{ rest: Pick, hover: Pick }} pick
 * @param {boolean} [keepHover] Leave a hover ink already there.
 */
function paint(texts, pick, keepHover = false) {
  for (const [name, { element }] of texts) {
    put(element, 'birrotInk', pick.rest[name]);
    if (!keepHover || !element.dataset.birrotInkHover) put(element, 'birrotInkHover', pick.hover[name]);
  }
}

/**
 * @param {HTMLElement} card
 * @param {Map<string, { element: HTMLElement }>} texts
 * @param {{ rest: Pick, hover: Pick }} pick
 */
function finish(card, texts, pick) {
  paint(texts, pick);
  markInked(card);
}

/**
 * From 990px Horizon scrolls `.page-wrapper`, not the document, and a
 * scroller clips what it holds before the viewport's margin applies; the
 * observer is rooted on whichever scrolls, so cards are read 200px early.
 */
function scroller() {
  const wrapper = document.querySelector('.page-wrapper');
  return wrapper && /auto|scroll/.test(getComputedStyle(wrapper).overflowY) ? wrapper : null;
}

/** @param {Element | null} root */
function observer(root) {
  const created = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        created.unobserve(entry.target);
        ink(/** @type {HTMLElement} */ (entry.target));
      }
    },
    { root, rootMargin: '200px 0px' }
  );
  return created;
}

let seen = observer(scroller());

/** @param {ParentNode} root */
function watch(root) {
  if (root instanceof Element && root.localName === 'product-card') return seen.observe(root);
  for (const card of root.querySelectorAll('product-card')) seen.observe(card);
}

watch(document);

new MutationObserver((records) => {
  for (const record of records) {
    if (record.type === 'childList') {
      for (const node of record.addedNodes) {
        if (node instanceof Element) watch(node);
      }
      continue;
    }
    // Only a morph removes these: the card shows new markup, maybe a new
    // product, and is read again when next near the viewport.
    const target = /** @type {Element} */ (record.target);
    if (target.hasAttribute(/** @type {string} */ (record.attributeName))) continue;
    const card = target.closest('product-card');
    if (!card) continue;
    latest.delete(card);
    seen.observe(card);
  }
}).observe(document.querySelector('main') ?? document.body, {
  childList: true,
  subtree: true,
  attributeFilter: ['data-birrot-ink', 'data-birrot-ink-hover', 'data-birrot-inked'],
});

// A variant moved the slideshow, or a chevron or swipe changed the photo
// (assets/birrot-card-photos.js). Before the first read, that read sees it.
for (const type of ['slideshow:select', 'birrot:card-photo']) {
  document.addEventListener(type, (event) => {
    const card = /** @type {Element} */ (event.target).closest?.('product-card');
    if (card instanceof HTMLElement && (latest.has(card) || card.hasAttribute('data-birrot-inked'))) ink(card);
  });
}

document.addEventListener('birrot:ink-cards', () => {
  for (const card of document.querySelectorAll('product-card')) ink(/** @type {HTMLElement} */ (card));
});

// After a change of width the cards are read again as they near the
// viewport, not all at once; a phone's address bar changes only the height.
// Crossing 990px changes what scrolls, and so the observer's root.
let width = window.innerWidth;
window.addEventListener(
  'resize',
  debounce(() => {
    if (window.innerWidth === width) return;
    width = window.innerWidth;
    const root = scroller();
    if (root !== seen.root) {
      seen.disconnect();
      seen = observer(root);
    }
    watch(document);
  }, 200)
);

// Text measured in a fallback font sits over another strip of the photo:
// once the webfonts are in, the cards are measured again.
document.fonts.ready.then(() => watch(document));
